import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Home, LogOut, UserCircle } from 'lucide-react';
import './UserDashboardSidebar.css';
import logo from '../components/assets/logo.png';

// Single source of truth for the user-account dashboard's sidebar nav.
// Used by UserFavorites.js, SentRequests.js and ProfileSettings.js so the
// three pages can never visually drift from each other again.
const NAV_ITEMS = [
  { key: 'favorites', label: 'Favorites', path: '/UserFavorites' },
  { key: 'requests', label: 'Sent Requests', path: '/SentRequests' },
  { key: 'settings', label: 'Settings', path: '/ProfileSettings' },
];

const UserDashboardSidebar = ({
  activeItem,
  visible,
  isMobile,
  onToggle,
  onClose,
  userName,
  profilePhoto,
  onHome,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [avatarLoadError, setAvatarLoadError] = useState(false);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [profilePhoto]);

  const goTo = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const sidebarClass = isMobile
    ? `uds-sidebar ${visible ? 'show' : ''}`
    : `uds-sidebar ${visible ? '' : 'hidden'}`;

  return (
    <>
      <button
        type="button"
        className={`uds-toggle-btn ${visible ? 'active' : ''}`}
        onClick={onToggle}
        aria-label="Toggle navigation menu"
      >
        <span className="uds-toggle-icon">
          {visible ? <X size={20} /> : <Menu size={20} />}
        </span>
      </button>

      {isMobile && (
        <div className={`uds-overlay ${visible ? 'active' : ''}`} onClick={onClose}></div>
      )}

      <div className={sidebarClass}>
        <div className="uds-logo-container">
          <img src={logo} alt="LitStudio" className="uds-logo" />
        </div>

        <nav className="uds-nav-menu">
          {NAV_ITEMS.map((item) => (
            <div
              key={item.key}
              className={`uds-nav-item ${activeItem === item.key ? 'is-active' : ''}`}
              onClick={() => goTo(item.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goTo(item.path);
                }
              }}
            >
              <span>{item.label}</span>
            </div>
          ))}
        </nav>

        <div className="uds-user-profile">
          <div className="uds-user-info">
            <div className="uds-user-avatar">
              {profilePhoto && !avatarLoadError ? (
                <img
                  src={profilePhoto}
                  alt={userName}
                  className="uds-user-avatar-image"
                  onError={() => setAvatarLoadError(true)}
                />
              ) : (
                <UserCircle size={20} />
              )}
            </div>
            <div className="uds-welcome-text">
              <div>{userName}</div>
              <small>Member</small>
            </div>
          </div>

          <div className="uds-user-actions">
            <button type="button" onClick={onHome} title="Go to Home">
              <Home size={15} />
              <span>Home</span>
            </button>
            <button type="button" className="uds-logout-btn" onClick={onLogout} title="Logout">
              <LogOut size={15} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDashboardSidebar;
